import { useCallback, useEffect, useRef, useState } from 'react'
import { ExternalLink, Loader2, CheckCircle, X, Layout, Zap } from 'lucide-react'
import { interfacesApi } from '@/services/studioService'
import { useNav } from '@/App'
import { useApp } from '@/store/AppContext'
import type { InterfaceBuildContract, InterfaceValidationResult, StudioConversationState, StudioInterfaceBuildState } from '@/types'
import './interface-suggestion-card.css'

export interface InterfaceSuggestionData {
  name: string
  description: string
  framework: 'react' | 'html'
  prompt: string
  components?: string[]
  color_scheme?: string
}

interface InterfaceSuggestionCardProps {
  data: InterfaceSuggestionData
  onDismiss?: () => void
}

export function InterfaceSuggestionCard({ data, onDismiss }: InterfaceSuggestionCardProps) {
  const [state, setState] = useState<'idle' | 'creating' | 'generating' | 'done' | 'error'>('idle')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [buildMessage, setBuildMessage] = useState<string | null>(null)
  const [validation, setValidation] = useState<InterfaceValidationResult | null>(null)
  const [contract, setContract] = useState<InterfaceBuildContract | null>(null)
  const { setStudioTab, setStudioOpen, setSurface } = useNav()
  const { activeConversation, updateConversationMeta, updateConversationStudioState } = useApp()
  const autoBuildStartedRef = useRef(false)

  const persistBuildState = useCallback((entry: StudioInterfaceBuildState) => {
    if (!activeConversation?.id) return
    const currentState: StudioConversationState = activeConversation.studioState ?? {}
    const existing = currentState.interfaceBuilds ?? []
    const nextBuilds = [
      entry,
      ...existing.filter(item => item.interfaceId !== entry.interfaceId),
    ].slice(0, 8)

    updateConversationStudioState(activeConversation.id, {
      ...currentState,
      version: 1,
      updatedAt: new Date().toISOString(),
      interfaceBuilds: nextBuilds,
    })
  }, [activeConversation, updateConversationStudioState])

  const safeData = {
    name: typeof data?.name === 'string' && data.name.trim() ? data.name : 'Nytt grensesnitt',
    description: typeof data?.description === 'string' && data.description.trim()
      ? data.description
      : 'Sine har laget et grensesnittforslag basert på samtalen.',
    framework: data?.framework === 'html' ? 'html' : 'react',
    prompt: typeof data?.prompt === 'string' ? data.prompt : '',
    components: Array.isArray(data?.components)
      ? data.components.filter((comp): comp is string => typeof comp === 'string' && comp.trim().length > 0)
      : [],
    color_scheme: typeof data?.color_scheme === 'string' ? data.color_scheme : '',
  }

  const handleBuild = useCallback(async () => {
    setState('creating')
    setErrorMsg(null)

    try {
      const iface = await interfacesApi.create({
        name: safeData.name,
        description: safeData.description,
        framework: safeData.framework,
        conversation_id: activeConversation?.id,
      })

      setState('generating')
      persistBuildState({
        interfaceId: iface.id,
        name: safeData.name,
        status: 'building',
        updatedAt: new Date().toISOString(),
        lastMessage: 'Studio genererer første versjon av grensesnittet.',
      })

      const result = await interfacesApi.generatePreview(iface.id, safeData.prompt)
      setPreviewUrl(result.preview_url)
      setValidation(result.validation ?? null)
      setContract(result.contract ?? null)
      setBuildMessage(result.user_message ?? null)
      setState('done')
      persistBuildState({
        interfaceId: iface.id,
        name: safeData.name,
        status: result.validation?.valid ? 'ready' : 'repairing',
        previewUrl: result.preview_url,
        contract: result.contract ?? null,
        validation: result.validation ?? null,
        lastMessage: result.user_message ?? 'Studio har gjort klar en trygg førsteversjon.',
        updatedAt: new Date().toISOString(),
      })

      window.dispatchEvent(new CustomEvent('sine:interface-created', {
        detail: {
          id: iface.id,
          preview_url: result.preview_url,
          conversation_id: activeConversation?.id,
        },
      }))

      if (activeConversation?.id) {
        updateConversationMeta(activeConversation.id, { appMode: 'studio', useAgentMode: false })
      }
      setSurface('studio')
      setStudioOpen(true)
      setStudioTab('interfaces')
    } catch (e) {
      setState('error')
      setErrorMsg(e instanceof Error ? e.message : 'Noe gikk galt')
      if (activeConversation?.id) {
        persistBuildState({
          interfaceId: `failed-${Date.now()}`,
          name: safeData.name,
          status: 'soft_failed',
          lastMessage: 'Studio klarte ikke å fullføre grensesnittet trygt denne gangen.',
          updatedAt: new Date().toISOString(),
        })
      }
    }
  }, [activeConversation?.id, persistBuildState, safeData.description, safeData.framework, safeData.name, safeData.prompt, setStudioOpen, setStudioTab, setSurface, updateConversationMeta])

  useEffect(() => {
    if (autoBuildStartedRef.current) return
    autoBuildStartedRef.current = true
    void handleBuild()
  }, [handleBuild])

  const handleOpenInStudio = () => {
    setSurface('studio')
    setStudioOpen(true)
    setStudioTab('interfaces')
  }

  if (state === 'done') {
    return (
      <div className="iface-card iface-card--done">
        <CheckCircle size={16} className="iface-card-done-icon" />
        <div className="iface-card-done-text">
          <span><strong>{safeData.name}</strong> er bygget og klar!</span>
          {buildMessage && <div className="iface-card-hint" style={{ marginTop: 6 }}>{buildMessage}</div>}
          {validation && !validation.valid && (
            <div className="iface-card-hint" style={{ marginTop: 6 }}>
              Studio har stabilisert førsteversjonen automatisk for å gjøre previewen tryggere.
            </div>
          )}
          <div className="iface-card-done-actions">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="iface-card-btn-primary"
              >
                <ExternalLink size={12} />
                Åpne preview
              </a>
            )}
            <button className="iface-card-btn-ghost" onClick={handleOpenInStudio}>
              <Layout size={12} />
              Se i Studio
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="iface-card">
      <div className="iface-card-header">
        <span className="iface-card-name">{safeData.name}</span>
        {onDismiss && (
          <button className="iface-card-dismiss" onClick={onDismiss} title="Avvis">
            <X size={13} />
          </button>
        )}
      </div>

      <p className="iface-card-desc">{safeData.description}</p>

      {safeData.components.length > 0 && (
        <div className="iface-card-components">
          {safeData.components.map((comp, i) => (
            <span key={i} className="iface-card-component-tag">{comp}</span>
          ))}
        </div>
      )}

      {safeData.color_scheme && (
        <div className="iface-card-meta">
          <Zap size={11} />
          <span>{safeData.color_scheme}</span>
        </div>
      )}

      {state === 'error' && errorMsg && (
        <div className="iface-card-error">{errorMsg}</div>
      )}

      {contract?.steps?.length ? (
        <div className="iface-card-components">
          {contract.steps.slice(0, 4).map(step => (
            <span key={step.id} className="iface-card-component-tag">{step.title}</span>
          ))}
        </div>
      ) : null}

      <div className="iface-card-actions">
        {state === 'error' && (
          <button
            className="iface-card-btn-primary"
            onClick={() => void handleBuild()}
            disabled={state === 'creating' || state === 'generating'}
          >
            Prøv igjen
          </button>
        )}
        {onDismiss && (
          <button className="iface-card-btn-ghost" onClick={onDismiss}>
            Avvis
          </button>
        )}
      </div>

      {(state === 'creating' || state === 'generating') && (
        <p className="iface-card-hint">
          {state === 'creating'
            ? 'Setter opp grensesnittet automatisk...'
            : 'AI genererer kode og starter en live sandkasse. Dette tar 10-30 sekunder.'}
        </p>
      )}

      {state === 'idle' && (
        <p className="iface-card-hint">
          Starter bygging automatisk...
        </p>
      )}

      {(state === 'creating' || state === 'generating') && (
        <div className="iface-card-actions">
          <button className="iface-card-btn-primary" disabled>
            <Loader2 size={12} className="iface-card-spin" />
            {state === 'creating' ? 'Oppretter...' : 'Bygger med AI...'}
          </button>
        </div>
      )}
    </div>
  )
}
